import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { GuideProvider } from '../../guides/GuideProvider';
import { ConsoleAssistant } from '../../assistant/ConsoleAssistant';
import { PushListener } from '../../components/PushListener';
import { useQuery } from '@tanstack/react-query';
import { inbox } from '@repletoryreach/sdk-api';
import { useDisclosure, useMediaQuery } from '@repletoryreach/sdk-utils/hooks';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { ScrollArea } from '@repletoryreach/sdk-ui/scroll';
import { Sidebar } from '../Sidebar';
import { HeaderBar } from '../HeaderBar';
import { RouteErrorBoundary } from '../../components/RouteErrorBoundary';
import { ImpersonationBanner } from '../../components/ImpersonationBanner';
import { EmailVerificationBanner } from '../../components/EmailVerificationBanner';
import { AgreementGate } from '../../components/AgreementGate';
import { RoleGate } from '../../components/RoleGate';
import { ClosingBanner } from '../../components/ClosingBanner';
import { useT } from '../../../i18n/I18nProvider';
import './shell.css';
import { BrandLogo } from '../../../components/BrandLogo';
import { BrandWordmark } from '../../../components/BrandWordmark';

/** ≤768px switches the sidebar to an off-canvas drawer (W33). Mirrors `shell.css`. */
export const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const DRAWER_WIDTH = 280;

/** Anchor for the skip link: see `_UI_improvements/tenant/_app-shell.md` §4 item 14. */
const MAIN_ID = 'rr-main';

/**
 * Authenticated tenant-console shell: fixed sidebar + header, scrolling main.
 *
 * At ≤768px the inline sidebar is not rendered; the same `<Sidebar>` mounts
 * inside an sdk-ui `<Drawer>` opened by the header burger (sdk-utils
 * `useDisclosure`). It closes on navigation and when the viewport grows.
 */
export function TenantShell() {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const [navOpened, { open: openNav, close: closeNav }] = useDisclosure(false);
  const { pathname } = useLocation();
  const t = useT();

  /**
   * Inbox unread badge (_UI_improvements/tenant/_app-shell.md §4 item 5).
   * The dashboard already computed this number; the sidebar: where an agent
   * actually looks: showed nothing. Shares the `['conversations']` key with
   * the inbox page, so it costs no extra request while that page is open.
   */
  const convs = useQuery({
    queryKey: ['conversations'],
    queryFn: () => inbox.listConversations({ limit: 50 }),
    select: (page) => (page.data ?? []).reduce((sum, c) => sum + (c.unread_count ?? 0), 0),
    staleTime: 30_000
  });
  const badges = useMemo(() => ({ '/inbox': convs.data ?? 0 }), [convs.data]);

  useEffect(() => { closeNav(); }, [pathname, closeNav]);
  useEffect(() => { if (!isMobile) closeNav(); }, [isMobile, closeNav]);

  return (
    <GuideProvider>
    <div className={`rr-shell${isMobile ? ' rr-shell--mobile' : ''}`}>
      {/* Keyboard users had to tab through all nine nav links on every page. */}
      <a className="rr-skip-link" href={`#${MAIN_ID}`}>{t('shell.skipToContent')}</a>

      {!isMobile && <Sidebar badges={badges} />}
      <div className="rr-shell__body">
        <ImpersonationBanner />
        <HeaderBar mobile={isMobile} menuOpened={navOpened} onMenuClick={openNav} />
        <ScrollArea as="main" className="rr-shell__main" viewportClassName="rr-shell__main-viewport" id={MAIN_ID} tabIndex={-1}>
          {/* Follows the user across every page, not just the dashboard. */}
          <EmailVerificationBanner />
          <ClosingBanner />
          {/* The data processing agreement comes before any page (2 October 2026). */}
          <AgreementGate>
            <RouteErrorBoundary>
              {/* Owner-and-admin pages are not for agents (2 October 2026). */}
              <RoleGate>
                <Outlet />
              </RoleGate>
            </RouteErrorBoundary>
          </AgreementGate>
        </ScrollArea>
      </div>
      {isMobile && (
        <Drawer
          opened={navOpened}
          onClose={closeNav}
          position="left"
          size={DRAWER_WIDTH}
          className="rr-shell__drawer"
          title={
            <span className="rr-shell__drawer-brand">
              <BrandLogo size={30} className="rr-shell__brand-mark" />
              <BrandWordmark className="rr-shell__brand-name" />
            </span>
          }
        >
          <Sidebar variant="drawer" onNavigate={closeNav} badges={badges} />
        </Drawer>
      )}
      {/* The console assistant, on every page (8 October 2026). */}
      <ConsoleAssistant />
      {/* Push alerts while the console is open (9 October 2026). */}
      <PushListener />
    </div>
    </GuideProvider>
  );
}
